import {
  Badge,
  Button,
  Card,
  Checkbox,
  Container,
  Divider,
  Flex,
  Grid,
  Group,
  LoadingOverlay,
  NumberInput,
  Paper,
  Select,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  IconArrowRight,
  IconClock,
  IconCoin,
  IconNote,
  IconPackage,
  IconRoute,
  IconWeight,
} from "@tabler/icons-react";
import axios from "axios";
import dayjs from "dayjs";
import { forwardRef, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import CardHeader from "@/components/CardHeader";
import CarListCard from "@/components/CarListCard";
import CompanySelector from "@/components/CompanySelector";
import ContactMismatchAlert from "@/components/ContactMismatchAlert";
import FileUploader from "@/components/FileUploader";
import FileUploaderWithoutPreview from "@/components/FileUploaderWithoutPreview";
import InternalCompanyDetailModalV2 from "@/components/InternalCompanyDetailModalV2";
import LocationFinder from "@/components/LocationFinder";
import usePartnerContactCheck from "@/hooks/usePartnerContactCheck";
import useAuthStore from "@/store/useAuthStore";
import {
  CAR_TYPE,
  CAR_WEIGHT,
  HOURS,
  KAKAO_PARSE_COMPANY_IDS,
  LOAD_TYPE,
  UNLOAD_TYPE,
} from "@/utils/constants";
import { getDistanceFromTmap } from "@/utils/functions";
import { parseMoney, parsePhone, resizeFile } from "@/utils/helpers";
import ItemSelectModal from "./ItemSelectModal";
import KakaoParseModal from "./KakaoParseModal";
import PartnerSelectModal from "./PartnerSelectModal";
import PriceTableModal from "./PriceTableModal";
import RecentCargoModal from "./RecentCargoModal";

const SelectItem = forwardRef(({ image, label, description, color, title, ...others }, ref) => (
  <div ref={ref} {...others}>
    <Group position="left">
      <Badge color={color} variant="filled" px={4}>
        {label}
      </Badge>
      <div>
        <Text size="sm">{title}</Text>
        <Text size="xs" opacity={0.65}>
          {description}
        </Text>
      </div>
    </Group>
  </div>
));

export default function New() {
  const navigate = useNavigate();
  const companyList = useAuthStore((state) => state.companyList);
  const { id } = useParams();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user.permission === "admin" || user.permission === "partner";
  const canUseKakaoParse =
    user.permission === "admin" || KAKAO_PARSE_COMPANY_IDS.includes(user?.company?.id);

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [memoCompanyId, setMemoCompanyId] = useState(null);

  const form = useForm({
    initialValues: {
      startName: "",
      startAddress: "",
      startAddressDetail: "",
      startLat: "",
      startLng: "",
      startPhoneNumber: "",

      endName: "",
      endAddress: "",
      endAddressDetail: "",
      endLat: "",
      endLng: "",
      endPhoneNumber: "",

      status: "requested",
      startType: "",
      endType: "",
      startDate: null,
      endDate: null,
      loadTime: "",
      unloadTime: "",

      productWeight: "",
      productContent: "",
      memo: "",
      // productImages - managed by file input
      productItem: {}, // for recent cargo modal
      carType: "",
      carWeight: "",
      loadType: "지게차",
      unloadType: "지게차",
      isReceiptRequired: false,
      isRoundTrip: false,
      isMixed: false,
      company: "",

      // 수출입 관련
      doId: null,
      icId: null, // import certificate

      // 거리 계산 관련
      distance: null,
      time: null,
      fare: null,

      isFeeOrder: false,
    },
    validate: {
      startName: (val) => !val && "상차지 이름을 입력해주세요",
      startAddress: (val) => !val && "상차지 주소를 입력해주세요",
      startPhoneNumber: (val) => !val && "상차지 담당자를 입력해주세요",
      startType: (val) => !val && "상차 일시를 선택해주세요",
      startDate: (val, values) => values.startType === "etc" && !val && "상차 일시를 선택해주세요",
      loadTime: (val) => !val && "상차 시간을 선택해주세요",
      endName: (val) => !val && "하차지 이름을 입력해주세요",
      endAddress: (val) => !val && "하차지 주소를 입력해주세요",
      endPhoneNumber: (val) => !val && "하차지 담당자를 입력해주세요",
      endType: (val) => !val && "하차 일시를 선택해주세요",
      endDate: (val, values) => values.endType === "etc" && !val && "하차 일시를 선택해주세요",
      unloadTime: (val, values) => values.endType !== "today" && !val && "하차 시간을 선택해주세요",
      productWeight: (val) => !val && "화물 상차 중량을 입력해주세요",
      productContent: (val) => !val && "화물 내용을 입력해주세요",
      carWeight: (val) => !val && "차량 톤수를 선택해주세요",
      carType: (val) => !val && "차종을 선택해주세요",
      loadType: (val) => !val && "상차 방법을 선택해주세요",
      unloadType: (val) => !val && "하차 방법을 선택해주세요",
      company: (val) => user.permission === "admin" && !val && "거래처를 선택해주세요",
    },
  });

  const availableLoadTimes = useMemo(() => {
    const baseOptions = [
      { value: "배차즉시", label: "배차즉시" },
      { value: "아무때나", label: "아무때나" },
    ];

    // 당일 상차가 아니면 모든 시간 표시
    if (form.values.startType !== "today") {
      return [...baseOptions, ...HOURS];
    }

    // 당일 상차: 현재 시간 이후만 필터링
    const now = dayjs();
    const currentMinutes = now.hour() * 60 + now.minute();

    // 30분 단위 올림 (14:25 → 14:30)
    const roundedMinutes = Math.ceil(currentMinutes / 30) * 30;

    const filteredHours = HOURS.filter((hour) => {
      const [h, m] = hour.value.split(":").map(Number);
      const hourMinutes = h * 60 + m;
      return hourMinutes >= roundedMinutes;
    });

    return [...baseOptions, ...filteredHours];
  }, [form.values.startType]);

  // 상차지 연락처 체크
  const {
    isMismatch: startContactMismatch,
    dbContact: startDbContact,
    dismiss: dismissStartAlert,
  } = usePartnerContactCheck({
    locationName: form.values.startName,
    phoneNumber: form.values.startPhoneNumber,
    companyId: user?.company?.id || form.values.company,
    enabled: !!form.values.startAddress,
  });

  // 하차지 연락처 체크
  const {
    isMismatch: endContactMismatch,
    dbContact: endDbContact,
    dismiss: dismissEndAlert,
  } = usePartnerContactCheck({
    locationName: form.values.endName,
    phoneNumber: form.values.endPhoneNumber,
    companyId: user?.company?.id || form.values.company,
    enabled: !!form.values.endAddress,
  });

  // 상차지 연락처 선택 핸들러
  const handleSelectStartFormContact = () => {
    dismissStartAlert();
  };

  const handleSelectStartDbContact = () => {
    form.setFieldValue("startPhoneNumber", startDbContact);
    dismissStartAlert();
  };

  // 하차지 연락처 선택 핸들러
  const handleSelectEndFormContact = () => {
    dismissEndAlert();
  };

  const handleSelectEndDbContact = () => {
    form.setFieldValue("endPhoneNumber", endDbContact);
    dismissEndAlert();
  };

  async function handleSubmit(vals) {
    const { startType, endType } = vals;

    if (user.permission === "admin" && !vals.company) return toast.error("거래처를 선택해주세요");
    if (!file && !vals?.productItem?.id) return toast.error("화물 사진을 업로드해주세요");

    setLoading(true);

    let company = user?.company?.id || vals.company;

    let _startDate = dayjs(vals.startDate).format("YYYY-MM-DD");
    let _endDate = dayjs(vals.endDate).format("YYYY-MM-DD");

    // 상차 일시 처리
    if (startType === "today") _startDate = dayjs().format("YYYY-MM-DD");
    if (startType === "tomorrow") _startDate = dayjs().add(1, "day").format("YYYY-MM-DD");
    if (startType === "monday")
      _startDate = dayjs()
        .add(8 - dayjs().day(), "day")
        .format("YYYY-MM-DD");
    if (startType === "etc") _startDate = dayjs(vals.startDate).format("YYYY-MM-DD");

    // 하차 일시 처리
    if (endType === "today") _endDate = dayjs().format("YYYY-MM-DD");
    if (endType === "tomorrow") _endDate = dayjs().add(1, "day").format("YYYY-MM-DD");
    if (endType === "monday")
      _endDate = dayjs()
        .add(8 - dayjs().day(), "day")
        .format("YYYY-MM-DD");
    if (endType === "etc") _endDate = dayjs(vals.endDate).format("YYYY-MM-DD");

    let unloadTime = vals.unloadTime;
    if (endType === "today") unloadTime = "바로당착";

    if (isAdmin) company = vals.company;

    const formData = new FormData();

    if (file) {
      const uri = await resizeFile(file);
      formData.append("files.productImages", uri, file.name);
      //remove productItem object from vals
      delete vals.productItem;
    }

    console.log(vals);

    const restData = JSON.stringify({
      ...vals,
      unloadTime,
      startDate: _startDate,
      endDate: _endDate,
      startLat: parseFloat(vals.startLat),
      startLng: parseFloat(vals.startLng),
      endLat: parseFloat(vals.endLat),
      endLng: parseFloat(vals.endLng),
      carWeight: vals.carWeight,
      company: company,
      user: id ? undefined : user.id,
      do: vals.doId,
      ic: vals.icId,
      isFeeOrder: vals.isFeeOrder || null,
      status: vals.isFeeOrder ? "feeRequested" : "requested",
      distance: vals.distance ?? null,
      time: vals.time ?? null,
      fare: vals.fare ?? null,
    });

    formData.append("data", restData);

    if (id) {
      // 수정 내역 체크
      let changedValues = "";
      if (form.isDirty("startName")) changedValues += "상차지 이름, ";
      if (form.isDirty("startAddress")) changedValues += "상차지 주소, ";
      if (form.isDirty("startAddressDetail")) changedValues += "상차지 세부주소, ";
      if (form.isDirty("startPhoneNumber")) changedValues += "상차지 담당자, ";
      if (form.isDirty("startType")) changedValues += "상차 일시, ";
      if (form.isDirty("startDate")) changedValues += "상차 날짜, ";
      if (form.isDirty("loadTime")) changedValues += "상차 시간, ";
      if (form.isDirty("endName")) changedValues += "하차지 이름, ";
      if (form.isDirty("endAddress")) changedValues += "하차지 주소, ";
      if (form.isDirty("endAddressDetail")) changedValues += "하차지 세부주소, ";
      if (form.isDirty("endPhoneNumber")) changedValues += "하차지 담당자, ";
      if (form.isDirty("endType")) changedValues += "하차 일시, ";
      if (form.isDirty("endDate")) changedValues += "하차 날짜, ";
      if (form.isDirty("unloadTime")) changedValues += "하차 시간, ";
      if (form.isDirty("productWeight")) changedValues += "화물 상차 중량, ";
      if (form.isDirty("productContent")) changedValues += "화물 내용, ";
      if (form.isDirty("memo")) changedValues += "기타 요청사항, ";
      if (form.isDirty("carWeight")) changedValues += "차량 톤수, ";
      if (form.isDirty("carType")) changedValues += "차종, ";
      if (form.isDirty("loadType")) changedValues += "상차 방법, ";
      if (form.isDirty("unloadType")) changedValues += "하차 방법, ";
      if (form.isDirty("isReceiptRequired")) changedValues += "인수증 필요, ";
      if (form.isDirty("isRoundTrip")) changedValues += "왕복, ";
      if (form.isDirty("isMixed")) changedValues += "혼적, ";
      if (form.isDirty("company")) changedValues += "거래처, ";
      if (form.isDirty("productItem")) changedValues += "화물 사진, ";
      if (form.isDirty("icId")) changedValues += "수출입 서류(입국신고필증), ";
      if (form.isDirty("doId")) changedValues += "수출입 서류(D/O), ";

      if (changedValues) {
        await axios({
          url: "/notifications",
          method: "post",
          data: {
            data: {
              name: "오더 수정",
              type: "order-edit",
              content: changedValues
                ? changedValues.slice(0, -2) + ` 수정 | (${user.name},${user.id})`
                : `수정 내용 없음 (${user.name},${user.id})`,
              requestUser: `${user.name} (${parsePhone(user.phoneNumber)})`,
              requestCompany:
                companyList.find((x) => x.id === Number(company))?.attributes?.name || "미소속",
              relationType: "order",
              relationId: id,
              isDone: false,
            },
          },
        });
      }
    }

    // console.log(vals?.productItem);
    // setLoading(false);
    // return;

    try {
      const { data } = await axios({
        url: id ? `/orders/${id}` : "/orders",
        method: id ? "PUT" : "POST",
        headers: { "Content-Type": "multipart/form-data" },
        data: formData,
      });

      // console.log(file);
      // console.log(vals?.productItem?.id);
      // setLoading(false);

      if (!file && !id) {
        await axios({
          url: `/orders/${data.data.id}`,
          method: "PUT",
          data: {
            data: {
              productImages: [vals.productItem.id],
            },
          },
        });
      }

      if (!file && id) {
        await axios({
          url: `/orders/${data.data.id}`,
          method: "PUT",
          data: {
            data: {
              productImages: [vals.productItem.id],
            },
          },
        });
      }

      Swal.fire({
        title: id ? "배차 수정 완료" : "배차 요청 완료",
        text: id ? "배차 수정이 완료되었습니다" : "배차 요청이 완료되었습니다",
        icon: "success",
        showCancelButton: false,
        confirmButtonColor: "#01467F",
        confirmButtonText: "배차 내역으로 이동",
        // cancelButtonText: "신규 배차 요청",
      }).then((result) => {
        if (!isAdmin) {
          navigate("/history");
          return;
        }

        if (vals.isFeeOrder) {
          navigate("/admin/orders/fee");
          return;
        }

        navigate("/admin/orders");
      });
    } catch (e) {
      console.log(e);
      const errMsg = e + " || " + e?.response?.data?.error?.message;
      alert(errMsg);
    } finally {
      setLoading(false);
    }
  }

  function clearStartInfo() {
    form.setValues({
      startName: "",
      startAddress: "",
      startAddressDetail: "",
      startPhoneNumber: "",
      startLat: "",
      startLng: "",
      isLoadPartner: false,
    });
  }

  function clearEndInfo() {
    form.setValues({
      endName: "",
      endAddress: "",
      endAddressDetail: "",
      endPhoneNumber: "",
      endLat: "",
      endLng: "",
      isUnloadPartner: false,
    });
  }

  async function fetchPrevOrder() {
    const { data } = await axios({
      url: `/orders/${id}?populate=*`,
      method: "GET",
    });

    if (data?.data?.attributes?.isFeeOrder && data?.data?.attributes?.status !== "feeRequested") {
      toast.error("배차 요청 상태가 아닙니다");
      navigate("/history");
      return;
    }

    // status check
    if (!data?.data?.attributes?.isFeeOrder && data?.data?.attributes?.status !== "requested") {
      toast.error("배차 요청 상태가 아닙니다");
      navigate("/history");
      return;
    }

    const prev = data?.data?.attributes || {};

    // console.log(prev);

    let startType = "etc";
    const prevStartDate = dayjs(prev.startDate).format("YYYY-MM-DD");
    if (prevStartDate === dayjs().format("YYYY-MM-DD")) startType = "today";
    if (prevStartDate === dayjs().add(1, "day").format("YYYY-MM-DD")) startType = "tomorrow";
    if (
      prevStartDate ===
      dayjs()
        .add(8 - dayjs().day(), "day")
        .format("YYYY-MM-DD")
    )
      startType = "monday";

    let endType = "etc";
    const prevEndDate = dayjs(prev.endDate).format("YYYY-MM-DD");
    if (prevEndDate === dayjs().format("YYYY-MM-DD")) endType = "today";
    if (prevEndDate === dayjs().add(1, "day").format("YYYY-MM-DD")) endType = "tomorrow";
    if (
      prevEndDate ===
      dayjs()
        .add(8 - dayjs().day(), "day")
        .format("YYYY-MM-DD")
    )
      endType = "monday";

    const prevData = {
      status: prev.status,
      startName: prev.startName,
      startAddress: prev.startAddress,
      startAddressDetail: prev.startAddressDetail,
      startPhoneNumber: prev.startPhoneNumber,
      startLat: prev.startLat,
      startLng: prev.startLng,
      // startType: prev.startType,
      startType,
      startDate: new Date(prev.startDate),
      loadTime: prev.loadTime,

      endName: prev.endName,
      endAddress: prev.endAddress,
      endAddressDetail: prev.endAddressDetail,
      endPhoneNumber: prev.endPhoneNumber,
      endLat: prev.endLat,
      endLng: prev.endLng,
      // endType: prev.endType,
      endType,
      endDate: new Date(prev.endDate),
      unloadTime: prev.unloadTime,

      productWeight: prev.productWeight,
      productContent: prev.productContent,
      memo: prev.memo,
      carType: prev.carType,
      carWeight: prev.carWeight,
      loadType: prev.loadType,
      unloadType: prev.unloadType,
      isReceiptRequired: prev.isReceiptRequired || false,
      isRoundTrip: prev.isRoundTrip || false,
      isMixed: prev.isMixed || false,
      company: prev?.company?.data?.id,
      productItem: prev?.productImages?.data?.[prev?.productImages?.data?.length - 1] || {},

      isLoadPartner: false, // 파트너로 추가시 true
      isUnloadPartner: false, // 파트너로 추가시 true

      icId: prev?.ic?.data?.id,
      doId: prev?.do?.data?.id,

      isFeeOrder: prev?.isFeeOrder || false,
    };

    form.setValues({
      ...prevData,
    });

    form.resetDirty({
      ...prevData,
    });
  }

  useEffect(() => {
    if (!id) return;
    fetchPrevOrder();
  }, [id]);

  useEffect(() => {
    if (form.values.endType === "today" && form.values.startType !== "today") {
      toast.error(
        "당착(당일 하차)는 당상(당일 상차)일 때만 가능합니다\n하차 일시를 다시 선택해주세요",
      );
      form.setFieldValue("endType", "");
    }
    // depend on both values that drive the rule
  }, [form.values.startType, form.values.endType]);

  useEffect(() => {
    if (id) return;
    if (!form.values.carWeight) return;
    // 바뀐 화물 중량을 그대로 실을 수 있는 톤수면 유지한다
    const selected = CAR_WEIGHT.find((x) => x.label === form.values.carWeight);
    if (form.values.productWeight && selected && form.values.productWeight <= selected.ton) return;
    toast.error("화물 중량 입력 후 차량 톤수를 다시 선택해주세요");
    form.setFieldValue("carWeight", "");
  }, [form.values.productWeight]);

  useEffect(() => {
    const sLat = Number(form.values.startLat);
    const sLng = Number(form.values.startLng);
    const eLat = Number(form.values.endLat);
    const eLng = Number(form.values.endLng);
    // check if all are numbers and not 0
    const valid = [sLat, sLng, eLat, eLng].every((x) => Number.isFinite(x) && x !== 0);
    if (!valid) {
      form.setValues({
        distance: "",
        time: "",
        fare: "",
      });
      return;
    }

    getDistanceFromTmap({
      startLat: sLat,
      startLng: sLng,
      endLat: eLat,
      endLng: eLng,
    })
      .then((res) => {
        const { totalDistance, totalTime, totalFare } = res;
        form.setValues({
          distance: totalDistance ? Number(totalDistance) : null,
          time: totalTime ? Number(totalTime) : null,
          fare: totalFare ? Number(totalFare) : null,
        });
      })
      .catch((err) => {
        console.log(err);
        form.setValues({
          distance: null,
          time: null,
          fare: null,
        });
      });
  }, [form.values.startLat, form.values.startLng, form.values.endLat, form.values.endLng]);

  return (
    <Container pb="md" pos="relative">
      <LoadingOverlay visible={loading} overlayBlur={2} />
      <InternalCompanyDetailModalV2
        companyId={memoCompanyId}
        close={() => setMemoCompanyId(null)}
      />

      <Grid>
        <Grid.Col span={12} md={9}>
          <Group mb="md">
            <Title order={2}>{id ? "배차 수정" : "신규 배차 요청"}</Title>
            <Flex gap="xs" align="center" wrap="wrap">
              {isAdmin && (
                <CompanySelector
                  form={form}
                  noLabel
                  mb={0}
                  // 버튼이 늘면서 Select가 눌려 거래처를 고를 수 없어, 좁은 화면에선 한 줄을 통째로 준다
                  sx={(theme) => ({
                    minWidth: 160,
                    [theme.fn.smallerThan("sm")]: { flex: "1 1 100%" },
                  })}
                />
              )}
              {canUseKakaoParse && (
                <KakaoParseModal
                  key={user?.company?.id || form.values.company}
                  companyId={user?.company?.id || form.values.company}
                  applyValues={form.setValues}
                />
              )}
              <RecentCargoModal form={form} companyId={user?.company?.id || form.values.company} />
            </Flex>
          </Group>

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Grid>
              <Grid.Col xs={12} md={6}>
                <Card>
                  <CardHeader
                    title="상차지 정보"
                    action={
                      <Flex gap="xs">
                        <Button
                          variant="outline"
                          disabled={!form.values.startAddress}
                          color="red"
                          compact
                          onClick={clearStartInfo}
                        >
                          다시 입력
                        </Button>
                      </Flex>
                    }
                  />

                  {!form.values.startAddress ? (
                    <>
                      <Text size="sm" component="label">
                        상차지 정보
                      </Text>
                      <Flex mb="sm" gap="sm">
                        <PartnerSelectModal
                          type="load"
                          title="내회사/상차지 목록"
                          style={{ flex: 1 }}
                          companyId={user?.company?.id || form.values.company}
                          form={form}
                        />

                        <LocationFinder
                          title="상차지 검색"
                          style={{ flex: 1 }}
                          selectItem={({
                            name,
                            address,
                            lat,
                            lng,
                            phone,
                            managerName,
                            managerPhoneNumber,
                            addressDetail,
                          }) => {
                            let managerInfo = managerPhoneNumber;
                            if (managerName) managerInfo += `(${managerName})`;
                            form.setValues({
                              startName: name,
                              startAddress: address,
                              startAddressDetail: addressDetail,
                              startPhoneNumber: managerInfo ? managerInfo : phone,
                              startLat: lat,
                              startLng: lng,
                            });
                          }}
                          companyId={user?.company?.id || form.values.company}
                        />
                      </Flex>
                    </>
                  ) : (
                    <>
                      <TextInput
                        label="상차지 이름"
                        placeholder="상차지 이름"
                        {...form.getInputProps("startName")}
                        description={form.values.startAddress}
                      />
                      <TextInput
                        label="상차지 세부주소"
                        placeholder="상차지 세부주소"
                        {...form.getInputProps("startAddressDetail")}
                      />
                      <TextInput
                        label="상차지 담당자"
                        placeholder="상차지 담당자"
                        {...form.getInputProps("startPhoneNumber")}
                      />

                      {/* 상차지 연락처 불일치 경고 */}
                      {startContactMismatch && (
                        <ContactMismatchAlert
                          formContact={form.values.startPhoneNumber}
                          dbContact={startDbContact}
                          onSelectFormContact={handleSelectStartFormContact}
                          onSelectDbContact={handleSelectStartDbContact}
                          onDismiss={dismissStartAlert}
                        />
                      )}
                    </>
                  )}

                  <Group mb="sm">
                    <Select
                      mb={0}
                      label="상차 일시"
                      placeholder="상차 일시 선택"
                      itemComponent={SelectItem}
                      data={LOAD_TYPE.filter((x) => x.value)}
                      maxDropdownHeight={400}
                      style={{ flex: 2 }}
                      {...form.getInputProps("startType")}
                    />

                    {form.values.startType === "etc" && (
                      <DatePickerInput
                        label="상차 날짜"
                        placeholder="상차 날짜 선택"
                        dropdownType="modal"
                        style={{ flex: 1 }}
                        minDate={isAdmin ? undefined : new Date()}
                        {...form.getInputProps("startDate")}
                      />
                    )}
                  </Group>

                  <Select
                    mb={0}
                    label="상차 시간"
                    placeholder="상차 시간 선택"
                    // description={
                    //   form.values.startType === "today" ? "현재 시간 이후의 시간만 선택 가능합니다" : undefined
                    // }
                    data={availableLoadTimes}
                    {...form.getInputProps("loadTime")}
                  />
                </Card>
              </Grid.Col>

              <Grid.Col xs={12} md={6}>
                <Card h="100%">
                  <CardHeader
                    title="하차지 정보"
                    action={
                      <Button
                        variant="outline"
                        disabled={!form.values.endAddress}
                        color="red"
                        compact
                        onClick={clearEndInfo}
                      >
                        다시 입력
                      </Button>
                    }
                  />

                  {!form.values.endAddress ? (
                    <>
                      <Text size="sm" component="label">
                        하차지 정보
                      </Text>
                      <Flex mb="sm" gap="sm">
                        <PartnerSelectModal
                          type="unload"
                          title="하차지 목록"
                          style={{ flex: 1 }}
                          companyId={user?.company?.id || form.values.company}
                          form={form}
                        />

                        <LocationFinder
                          title="하차지 검색"
                          style={{ flex: 1 }}
                          selectItem={({
                            name,
                            address,
                            lat,
                            lng,
                            phone,
                            managerName,
                            managerPhoneNumber,
                            addressDetail,
                          }) => {
                            let managerInfo = managerPhoneNumber;
                            if (managerName) managerInfo += `(${managerName})`;
                            form.setValues({
                              endName: name,
                              endAddress: address,
                              endAddressDetail: addressDetail,
                              endPhoneNumber: managerInfo ? managerInfo : phone,
                              endLat: lat,
                              endLng: lng,
                            });
                          }}
                          companyId={user?.company?.id || form.values.company}
                        />
                      </Flex>
                    </>
                  ) : (
                    <>
                      <TextInput
                        label="하차지 이름"
                        placeholder="하차지 이름"
                        {...form.getInputProps("endName")}
                        description={form.values.endAddress}
                      />
                      <TextInput
                        label="하차지 세부주소"
                        placeholder="하차지 세부주소"
                        {...form.getInputProps("endAddressDetail")}
                      />
                      <TextInput
                        label="하차지 담당자"
                        placeholder="하차지 담당자"
                        {...form.getInputProps("endPhoneNumber")}
                      />

                      {/* 하차지 연락처 불일치 경고 */}
                      {endContactMismatch && (
                        <ContactMismatchAlert
                          formContact={form.values.endPhoneNumber}
                          dbContact={endDbContact}
                          onSelectFormContact={handleSelectEndFormContact}
                          onSelectDbContact={handleSelectEndDbContact}
                          onDismiss={dismissEndAlert}
                        />
                      )}
                    </>
                  )}

                  <Group mb="sm">
                    <Select
                      mb={0}
                      label="하차 일시"
                      placeholder="하차 일시 선택"
                      itemComponent={SelectItem}
                      data={UNLOAD_TYPE.filter((x) => x.value)?.filter((x) => {
                        if (form.values.startType !== "today") return x.value !== "today";
                        return true;
                      })}
                      maxDropdownHeight={400}
                      style={{ flex: 2 }}
                      {...form.getInputProps("endType")}
                    />
                    {form.values.endType === "etc" && (
                      <DatePickerInput
                        label="하차 날짜"
                        placeholder="하차 날짜 선택"
                        dropdownType="modal"
                        style={{ flex: 1 }}
                        minDate={isAdmin ? undefined : new Date()}
                        {...form.getInputProps("endDate")}
                      />
                    )}
                  </Group>

                  <Select
                    mb={0}
                    label="하차 시간"
                    placeholder={
                      form.values.endType === "today"
                        ? "상차 후 바로 목적지로 이동합니다"
                        : "하차 시간 선택"
                    }
                    disabled={form.values.endType === "today"}
                    data={[{ value: "상차 후 바로", label: "상차 후 바로" }, ...HOURS]}
                    {...form.getInputProps("unloadTime")}
                  />
                </Card>
              </Grid.Col>

              <Grid.Col xs={12} md={6}>
                <Card>
                  <CardHeader title="화물 정보" />

                  <NumberInput
                    label="화물 상차 중량 (단위: 톤)"
                    placeholder="숫자만 입력 - 단위: 톤(ton)"
                    max={27}
                    step={0.1}
                    min={0}
                    precision={1}
                    style={{ flex: 1 }}
                    icon={<IconWeight size="1rem" />}
                    {...form.getInputProps("productWeight")}
                  />
                  <TextInput
                    icon={<IconPackage size="1rem" />}
                    label="화물 내용"
                    style={{ flex: 1 }}
                    placeholder="화물 상세 내용 입력"
                    {...form.getInputProps("productContent")}
                  />
                  <TextInput
                    icon={<IconNote size="1rem" />}
                    label="기타 요청사항"
                    style={{ flex: 1 }}
                    placeholder="기타 요청사항 내용 입력"
                    {...form.getInputProps("memo")}
                  />

                  <Text size="sm" mt={2} mb={2}>
                    화물(상차품) 사진
                  </Text>
                  {!form?.values?.productItem?.attributes?.url && !file && (
                    <Flex gap="sm">
                      <ItemSelectModal
                        companyId={user?.company?.id || form.values.company}
                        form={form}
                      />
                      <FileUploader
                        file={file}
                        setFile={setFile}
                        title="화물 사진 검색"
                        accept="image/png,image/jpeg"
                        url={form?.values?.productItem?.attributes?.url}
                        removeUrl={() => form.setFieldValue("productItem", {})}
                      />
                    </Flex>
                  )}

                  {file && (
                    <FileUploader
                      file={file}
                      setFile={setFile}
                      title="화물(상차품) 사진"
                      accept="image/png,image/jpeg"
                      url={form?.values?.productItem?.attributes?.url}
                      removeUrl={() => form.setFieldValue("productItem", {})}
                    />
                  )}

                  {form?.values?.productItem?.attributes?.url && (
                    <FileUploader
                      file={file}
                      setFile={setFile}
                      title="화물(상차품) 사진"
                      accept="image/png,image/jpeg"
                      url={form?.values?.productItem?.attributes?.url}
                      removeUrl={() => form.setFieldValue("productItem", {})}
                    />
                  )}

                  <Text size="sm" mt="xs" mb={2}>
                    수출입 서류 (포워더 전용)
                  </Text>
                  <Flex gap="sm">
                    <FileUploaderWithoutPreview
                      form={form}
                      title="수출입신고필증"
                      formId="icId"
                      fileId={form.values.icId}
                    />
                    <FileUploaderWithoutPreview
                      form={form}
                      title="D/O"
                      formId="doId"
                      fileId={form.values.doId}
                    />
                  </Flex>
                </Card>
              </Grid.Col>
              <Grid.Col xs={12} md={6}>
                <Card h="100%">
                  <CardHeader title="차량 정보" />
                  <Flex gap="sm">
                    <Select
                      label="톤수"
                      placeholder={
                        form.values.productWeight ? "톤수 선택" : "화물 중량 입력 후 선택"
                      }
                      data={CAR_WEIGHT.map((item) => ({
                        value: item.label,
                        label: item.label,
                        disabled: form.values.productWeight > item.ton,
                      }))}
                      disabled={!form.values.productWeight}
                      {...form.getInputProps("carWeight")}
                      style={{ flex: 1 }}
                    />
                    <Select
                      label="차종"
                      placeholder={
                        form.values.productWeight ? "차종 선택" : "화물 중량 입력 후 선택"
                      }
                      data={CAR_TYPE}
                      {...form.getInputProps("carType")}
                      disabled={!form.values.productWeight}
                      style={{ flex: 1 }}
                    />
                  </Flex>
                  <Flex gap="sm">
                    <Select
                      label="상차 방법"
                      placeholder="상차 방법 선택"
                      data={["지게차", "수작업", "크레인", "호이스트"]}
                      {...form.getInputProps("loadType")}
                      style={{ flex: 1 }}
                    />
                    <Select
                      label="하차 방법"
                      placeholder="하차 방법 선택"
                      data={["지게차", "수작업", "크레인", "호이스트"]}
                      {...form.getInputProps("unloadType")}
                      style={{ flex: 1 }}
                    />
                  </Flex>
                  <Select
                    label="지불 방식"
                    placeholder="지불 방식 선택"
                    data={[
                      { label: "인수증(신용 거래)", value: false },
                      { label: "선착불", value: true },
                    ]}
                    {...form.getInputProps("isFeeOrder")}
                  />

                  <Flex my="md" gap="sm">
                    <Checkbox
                      label="인수증 필요"
                      {...form.getInputProps("isReceiptRequired", { type: "checkbox" })}
                    />
                    <Checkbox
                      label="왕복"
                      {...form.getInputProps("isRoundTrip", { type: "checkbox" })}
                    />
                    <Checkbox
                      label="혼적"
                      {...form.getInputProps("isMixed", { type: "checkbox" })}
                    />
                  </Flex>

                  {form.values.distance && (
                    <>
                      <Title order={6} mb={4}>
                        예상 거리/시간/통행료
                      </Title>
                      <Paper withBorder p="xs">
                        <Flex>
                          <Flex style={{ flex: 1 }} align="center" gap={4} justify="center">
                            <IconRoute size={16} />
                            <Text size="sm">{form.values.distance}km</Text>
                          </Flex>
                          <Divider orientation="vertical" />
                          <Flex style={{ flex: 1 }} align="center" gap={4} justify="center">
                            <IconClock size={16} />
                            <Text size="sm">{form.values.time}분</Text>
                          </Flex>
                          <Divider orientation="vertical" />
                          <Flex style={{ flex: 1 }} align="center" gap={4} justify="center">
                            <IconCoin size={16} />
                            <Text size="sm">{parseMoney(form.values.fare)}원</Text>
                          </Flex>
                        </Flex>
                      </Paper>
                    </>
                  )}
                </Card>
              </Grid.Col>
            </Grid>

            <Flex mt="md" gap="xs" justify="flex-end" style={{ flexWrap: "wrap" }}>
              {user.permission === "admin" && (
                <>
                  <Button
                    size="md"
                    color="orange"
                    onClick={() => setMemoCompanyId(form.values.company)}
                    disabled={!form.values.company}
                  >
                    메모
                  </Button>
                  <PriceTableModal companyId={form.values.company || user?.company?.id} />
                </>
              )}
              <Button
                size="md"
                type="submit"
                rightIcon={<IconArrowRight size={18} />}
                loading={loading}
                disabled={!form.values.startAddress || !form.values.endAddress}
              >
                {id ? "배차 수정" : "배차 요청"}
              </Button>
            </Flex>
          </form>
        </Grid.Col>

        <Grid.Col span={12} md={3}>
          <CarListCard />
        </Grid.Col>
      </Grid>
    </Container>
  );
}
